import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EMAIL_PATTERN } from '../../constants/validation-patterns';
import { AppState } from '../../app.service';
import { ProfileService } from '../../../services/profile.service';
import { ToastHelperService } from '../../../services/toast-helper.service';
import { AuthService } from '../../../services/auth.service';
import { ModalService } from '../../../services/modal.service';

export interface ChangeEmailModalData {
  userEmail: string;
  customClass?: string;
}

@Component({
  selector: 'app-change-email-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './change-email-modal.component.html',
  styleUrl: './change-email-modal.component.scss'
})
export class ChangeEmailModalComponent {
  public emailForm: FormGroup;
  public credentialsError = false;
  public showPassword = false;
  public hideModal = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ChangeEmailModalData,
    private dialogRef: MatDialogRef<ChangeEmailModalComponent>,
    private appState: AppState,
    private profileService: ProfileService,
    private toastHelper: ToastHelperService,
    private translate: TranslateService,
    private authService: AuthService,
    private modalService: ModalService
  ) {
    this.emailForm = new FormGroup({
      email: new FormControl(this.data?.userEmail ?? '', [Validators.required, Validators.pattern(EMAIL_PATTERN)]),
      password: new FormControl('', [Validators.required])
    });
  }

  public updateEmail() {
    this.emailForm.markAllAsTouched();
    if (this.emailForm.invalid) {
      return;
    }

    this.appState.loading = true;
    const newEmail = this.emailForm.controls['email'].value;
    const password = this.emailForm.controls['password'].value;
    if (this.data.userEmail !== newEmail) {
      this.appState.loading = true;
      this.authService.login(this.data.userEmail, password).then(() => {
        this.profileService.changeEmail({ newEmail: newEmail }).then(() => {
          this.authService.login(newEmail, password).then(() => {
            this.appState.loading = false;
            this.closeDialog(newEmail);
          }).catch((error) => {
            this.appState.loading = false;
            this.errorModal(error.error.errors[0].message);
          });
        }).catch((error) => {
          this.appState.loading = false;
          this.errorModal(error.error.errors[0].message);
        });
      }).catch((err) => {
        this.appState.loading = false;
        const error = err.error.errors[0];
        this.appState.loading = false;
        if (error.type === 'Validation Error' || error?.textCode === 'INVALID_LOGIN_CREDENTIALS') {
          this.credentialsError = true;
        } else {
          this.errorModal(error.message);
        }
      });
    } else {
      this.appState.loading = false;
      this.toastHelper.warn(this.translate.instant('change-email-modal.use-different-email'));
    }
  }

  errorModal(message?: string) {
    this.hideModal = true;
    this.modalService.failureModal(
      {
        msg: message || this.translate.instant('failure-modal.request-cannot-be-processed'),
        buttonText: this.translate.instant('failure-modal.got-it'),
        title: this.translate.instant('failure-modal.something-went-wrong'),
      }
    ).afterClosed().subscribe((res) => {
          this.hideModal = false;
     });
  }

  beforeClose(): boolean {
    if (document.body.classList.contains('modal-open')) {
      document.body.classList.remove('modal-open');
    }
    return false;
  }

  closeDialog(res?: string): void {
    this.beforeClose();
    this.dialogRef.close(res);
  }

  cancel(): void {
    this.closeDialog();
  }
}
